import axios from "axios";
import { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Card,
    Input,
    Row,
    Col,
    Select,
    Button,
    message,
    Modal,
    Form,
    InputNumber,
} from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { FilePdfOutlined, FileExcelOutlined, EditOutlined, DownloadOutlined } from "@ant-design/icons";

/* ─────────────────────────────────────────────────────────────────────────────
   SALARY_TEMPLATE_HEADERS — exact column order expected by the bulk-upload
   endpoint. Kept in sync with ActualSalaryStatement (Monthly Salary Statement).
   Download Template produces a blank workbook with ONLY these headers so the
   user can fill in data and upload via the Monthly Salary Statement page.
───────────────────────────────────────────────────────────────────────────── */
const SALARY_TEMPLATE_HEADERS = [
    "employee_code",
    "emp_name",
    "designation",
    "department",
    "doj",
    "bank_name",
    "ifsc_code",
    "account_no",
    "bank_branch",
    "pf_uan",
    "esci_no",
    "pan_no",
    "aadhar_no",
    "month_days",
    "days_payable",
    "basic_pm",
    "hra_pm",
    "conveyance_pm",
    "actual_other_allowance",
    "fixed_other_allowance",
    "special_allowance_pm",
    "ot_pay_pm",
    "arrears_pm",
    "basic_earned",
    "hra_earned",
    "conveyance_earned",
    "special_allowance_earned",
    "gross_salary",
    "gross_earned",
    "flexible_benefit_allowance",
    "fixed_earning",
    "actual_earning",
    "pf_employer",
    "esi_employer",
    "pf_employee",
    "esi_employee",
    "late_deduction",
    "salary_advance",
    "other_deductions",
    "pt_pm",
    "tds",
    "total_deductions",
    "net_pay",
    "ctc_monthly",
    "no_of_ot_days",
    "late_days",
    "lop_days",
    "lop_deduction",
    "ot_pay",
    "arrears",
    "Remarks",
    "Salary Month",
    "Salary Type",
];



export default function FixedSalary() {
    const [data, setData] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [orgList, setOrgList] = useState([]);
    const [deptList, setDeptList] = useState([]);
    const [desigList, setDesigList] = useState([]);

    const { backUrl, authData } = useContext(AuthContext);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        organisation: "",
        department: "",
        designation: "",
    });

    // Modal & Form states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [form] = Form.useForm();
    const [dirtyFields, setDirtyFields] = useState(new Set());

    useEffect(() => {
        const fetchFiltersData = async () => {
            try {
                const headers = {
                    Authorization: `Bearer ${authData?.access}`,
                    "Content-Type": "application/json",
                };

                const [orgRes, deptRes, desigRes] = await Promise.all([
                    axios.get(`${backUrl}/api/emp/organisations/`, { headers }),
                    axios.get(`${backUrl}/api/emp/departments/`, { headers }),
                    axios.get(`${backUrl}/api/emp/designations/`, { headers }),
                ]);

                setOrgList(orgRes.data.results || []);
                setDeptList(deptRes.data.results || []);
                setDesigList(desigRes.data.results || []);

            } catch (err) {
                console.error("Filter dropdown fetch error:", err);
            }
        };

        fetchFiltersData();
    }, []);

    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/payroll/salaries/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                    "Content-Type": "application/json",
                },
            });
            const results = res.data.results || [];
            setData(results);
        } catch (error) {
            console.error("Error fetching salary statement:", error);
            toast.error("Failed to fetch data");
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredData = useMemo(() => {
        let filtered = [...data];

        if (filters.emp_code) {
            filtered = filtered.filter((item) =>
                item.employee?.emp_code?.toLowerCase().includes(filters.emp_code.toLowerCase())
            );
        }
        if (filters.name) {
            filtered = filtered.filter((item) => {
                const fullName = `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`.trim().toLowerCase();
                return fullName.includes(filters.name.toLowerCase());
            });
        }
        if (filters.organisation) {
            const searchOrg = filters.organisation.toLowerCase();
            filtered = filtered.filter(item =>
                item.employee?.organisation_name?.toLowerCase().includes(searchOrg)
            );
        }
        if (filters.department) {
            const searchDept = filters.department.toLowerCase();
            filtered = filtered.filter(item =>
                item.employee?.department_name?.toLowerCase().includes(searchDept)
            );
        }
        if (filters.designation) {
            const searchDesig = filters.designation.toLowerCase();
            filtered = filtered.filter(item =>
                item.employee?.designation_name?.toLowerCase().includes(searchDesig)
            );
        }

        return filtered;
    }, [data, filters]);

    const handleOpenModal = (record) => {
        setSelectedRecord(record);
        // Only set PM values, PY will be calculated
        const pmValues = {
            basic_pm: record.basic_pm,
            hra_pm: record.hra_pm,
            conveyance_pm: record.conveyance_pm,
            other_allowance_pm: record.other_allowance_pm,
            gross_pm: record.gross_pm,
            pf_employer_pm: record.pf_employer_pm,
            esi_employer_pm: record.esi_employer_pm,
            ctc_pm: record.ctc_pm,
            pf_employee_pm: record.pf_employee_pm,
            esi_employee_pm: record.esi_employee_pm,
            pt_pm: record.pt_pm,
            deductions_pm: record.deductions_pm,
            net_pay_pm: record.net_pay_pm,
        };
        form.setFieldsValue(pmValues);
        setDirtyFields(new Set());
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setSelectedRecord(null);
        setIsModalVisible(false);
        form.resetFields();
        setDirtyFields(new Set());
    };

    const onValuesChange = (changedValues) => {
        const updatedDirtyFields = new Set(dirtyFields);
        Object.keys(changedValues).forEach((key) => updatedDirtyFields.add(key));
        setDirtyFields(updatedDirtyFields);
    };

    const handleSubmit = async () => {
        try {
            const values = form.getFieldsValue();
            
            // Calculate PY values (PM * 12)
            const updatedValues = {
                ...values,
                basic_py: (values.basic_pm || 0) * 12,
                hra_py: (values.hra_pm || 0) * 12,
                conveyance_py: (values.conveyance_pm || 0) * 12,
                other_allowance_py: (values.other_allowance_pm || 0) * 12,
                gross_py: (values.gross_pm || 0) * 12,
                pf_employer_py: (values.pf_employer_pm || 0) * 12,
                esi_employer_py: (values.esi_employer_pm || 0) * 12,
                ctc_py: (values.ctc_pm || 0) * 12,
                pf_employee_py: (values.pf_employee_pm || 0) * 12,
                esi_employee_py: (values.esi_employee_pm || 0) * 12,
                pt_py: (values.pt_pm || 0) * 12,
                deductions_py: (values.deductions_pm || 0) * 12,
                net_pay_py: (values.net_pay_pm || 0) * 12,
            };

            message.loading({ content: "Updating salary...", key: "modalSubmit" });
            
            // FixedSalary page deals with the base Salary model, not the ActualSalaryStructure (drafts).
            // The correct endpoint for base salary updates is /salary_update/<id>/
            const res = await axios.patch(
                `${backUrl}/api/payroll/salary_update/${selectedRecord.id}/`,
                updatedValues,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            message.destroy("modalSubmit");
            message.success("Salary updated successfully!");
            toast.success(res.data.message || "Salary updated successfully!");
            fetchData();
            handleCloseModal();
        } catch (error) {
            console.error("Update salary error:", error);
            message.destroy("modalSubmit");
            message.error("Failed to update salary!");
            toast.error("Failed to update salary!");
        }
    };

    const columns = [
        {
            title: "Emp Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            fixed: "left",
        },
        {
            title: "Name",
            key: "employee_name",
            fixed: "left",
            render: (record) =>
                `${record.employee?.first_name || ""} ${record.employee?.last_name || ""}`,
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "organisation_name", width: 200 },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "department_name", width: 200 },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "designation_name", width: 200 },
        { title: "Is PF", dataIndex: "is_pf", key: "is_pf", render: (val) => val ? "Yes" : "No" },
        { title: "Is ESI", dataIndex: "is_esi", key: "is_esi", render: (val) => val ? "Yes" : "No" },
        { title: "Basic PM", dataIndex: "basic_pm", key: "basic_pm" },
        { title: "HRA PM", dataIndex: "hra_pm", key: "hra_pm" },
        { title: "Conveyance PM", dataIndex: "conveyance_pm", key: "conveyance_pm" },
        { title: "Other Allowance PM", dataIndex: "other_allowance_pm", key: "other_allowance_pm" },
        { title: "Gross PM", dataIndex: "gross_pm", key: "gross_pm" },
        { title: "PF Employer PM", dataIndex: "pf_employer_pm", key: "pf_employer_pm" },
        { title: "ESI Employer PM", dataIndex: "esi_employer_pm", key: "esi_employer_pm" },
        { title: "CTC PM", dataIndex: "ctc_pm", key: "ctc_pm" },
        { title: "PF Employee PM", dataIndex: "pf_employee_pm", key: "pf_employee_pm" },
        { title: "ESI Employee PM", dataIndex: "esi_employee_pm", key: "esi_employee_pm" },
        { title: "PT PM", dataIndex: "pt_pm", key: "pt_pm" },
        { title: "Deductions PM", dataIndex: "deductions_pm", key: "deductions_pm" },
        { title: "Net Pay PM", dataIndex: "net_pay_pm", key: "net_pay_pm" },
        { title: "Basic PY", dataIndex: "basic_py", key: "basic_py" },
        { title: "HRA PY", dataIndex: "hra_py", key: "hra_py" },
        { title: "Conveyance PY", dataIndex: "conveyance_py", key: "conveyance_py" },
        { title: "Other Allowance PY", dataIndex: "other_allowance_py", key: "other_allowance_py" },
        { title: "Gross PY", dataIndex: "gross_py", key: "gross_py" },
        { title: "PF Employer PY", dataIndex: "pf_employer_py", key: "pf_employer_py" },
        { title: "ESI Employer PY", dataIndex: "esi_employer_py", key: "esi_employer_py" },
        { title: "CTC PY", dataIndex: "ctc_py", key: "ctc_py" },
        { title: "PF Employee PY", dataIndex: "pf_employee_py", key: "pf_employee_py" },
        { title: "ESI Employee PY", dataIndex: "esi_employee_py", key: "esi_employee_py" },
        { title: "PT PY", dataIndex: "pt_py", key: "pt_py" },
        { title: "Deductions PY", dataIndex: "deductions_py", key: "deductions_py" },
        { title: "Net Pay PY", dataIndex: "net_pay_py", key: "net_pay_py" },
        {
            title: "Action",
            key: "action",
            fixed: "right",
            width: 80,
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => handleOpenModal(record)}
                    style={{
                        backgroundColor: "#0e497b",
                        borderColor: "#0e497b",
                    }}
                    size="small"
                />
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    /* ─── Download Template (headers-only, matches upload endpoint) ─── */
    const handleDownloadTemplate = () => {
        try {
            const ws = XLSX.utils.aoa_to_sheet([SALARY_TEMPLATE_HEADERS]);
            ws["!cols"] = SALARY_TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(12, h.length + 2) }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Salary Upload");
            XLSX.writeFile(wb, "Salary_Upload_Template.xlsx");
            toast.success("Template downloaded successfully!");
        } catch (error) {
            console.error("Error downloading template:", error);
            toast.error("Failed to download template");
        }
    };

    const handleExportExcel = () => {
        if (!filteredData.length) {
            toast.warning("No data to export!");
            return;
        }

        const exportData = filteredData.map((item) => ({
            EmpCode: item.employee?.emp_code,
            Name: `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`,
            Organisation: item.employee?.organisation_name,
            Department: item.employee?.department_name,
            Designation: item.employee?.designation_name,
            IsPF: item.is_pf ? "Yes" : "No",
            IsESI: item.is_esi ? "Yes" : "No",
            BasicPM: item.basic_pm,
            HRAPM: item.hra_pm,
            ConveyancePM: item.conveyance_pm,
            OtherAllowancePM: item.other_allowance_pm,
            GrossPM: item.gross_pm,
            PFEmployerPM: item.pf_employer_pm,
            ESIEmployerPM: item.esi_employer_pm,
            CTCPM: item.ctc_pm,
            PFEmployeePM: item.pf_employee_pm,
            ESIEmployeePM: item.esi_employee_pm,
            PTPM: item.pt_pm,
            DeductionsPM: item.deductions_pm,
            NetPayPM: item.net_pay_pm,
            BasicPY: item.basic_py,
            HRAPY: item.hra_py,
            ConveyancePY: item.conveyance_py,
            OtherAllowancePY: item.other_allowance_py,
            GrossPY: item.gross_py,
            PFEmployerPY: item.pf_employer_py,
            ESIEmployerPY: item.esi_employer_py,
            CTCPY: item.ctc_py,
            PFEmployeePY: item.pf_employee_py,
            ESIEmployeePY: item.esi_employee_py,
            PTPY: item.pt_py,
            DeductionsPY: item.deductions_py,
            NetPayPY: item.net_pay_py,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "FixedSalary");

        XLSX.writeFile(workbook, "fixed_salary.xlsx");
    };

    const handleExportPDF = () => {
        if (!filteredData.length) {
            toast.warning("No data to export!");
            return;
        }

        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "a3",
        });

        const exportData = filteredData.map((item) => [
            item.employee?.emp_code,
            `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`,
            item.employee?.organisation_name,
            item.employee?.department_name,
            item.employee?.designation_name,
            item.is_pf ? "Yes" : "No",
            item.is_esi ? "Yes" : "No",
            item.basic_pm,
            item.hra_pm,
            item.conveyance_pm,
            item.other_allowance_pm,
            item.gross_pm,
            item.pf_employer_pm,
            item.esi_employer_pm,
            item.ctc_pm,
            item.pf_employee_pm,
            item.esi_employee_pm,
            item.pt_pm,
            item.deductions_pm,
            item.net_pay_pm,
            item.basic_py,
            item.hra_py,
            item.conveyance_py,
            item.other_allowance_py,
            item.gross_py,
            item.pf_employer_py,
            item.esi_employer_py,
            item.ctc_py,
            item.pf_employee_py,
            item.esi_employee_pm,
            item.pt_py,
            item.deductions_py,
            item.net_pay_py,
        ]);

        const headers = [
            "Emp Code", "Name", "Organisation", "Department", "Designation",
            "Is PF", "Is ESI", "Basic PM", "HRA PM", "Conveyance PM",
            "Other Allowance PM", "Gross PM", "PF Employer PM", "ESI Employer PM",
            "CTC PM", "PF Employee PM", "ESI Employee PM", "PT PM", "Deductions PM",
            "Net Pay PM", "Basic PY", "HRA PY", "Conveyance PY", "Other Allowance PY",
            "Gross PY", "PF Employer PY", "ESI Employer PY", "CTC PY", "PF Employee PY",
            "ESI Employee PM", "PT PY", "Deductions PY", "Net Pay PY"
        ];

        doc.text("Fixed Salary Report", 40, 40);

        autoTable(doc, {
            startY: 60,
            head: [headers],
            body: exportData,
            styles: { fontSize: 7 },
            overflow: "linebreak",
            tableWidth: "wrap",
            headStyles: { fillColor: [14, 73, 123] },
            pageBreak: "auto",
            horizontalPageBreak: true,
            horizontalPageBreakRepeat: true,
        });

        doc.save("fixed_salary.pdf");
    };

    return (
        <>
            <Modal
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                title={`Edit Salary – ${selectedRecord?.employee?.emp_code || ""} – ${selectedRecord?.employee?.first_name || ""}`}
                width={900}
            >
                {selectedRecord && (
                    <Form
                        layout="vertical"
                        form={form}
                        onValuesChange={onValuesChange}
                    >
                        <div style={{ display: 'flex', width: '100%', marginBottom: '20px', borderBottom: '2px solid #0e497b', paddingBottom: '8px' }}>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <span style={{ color: '#0e497b', fontWeight: '800', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>Monthly Structure</span>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <span style={{ color: '#0e497b', fontWeight: '800', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>Yearly Projection</span>
                            </div>
                        </div>
                        <Row gutter={[24, 16]}>
                            {/* Basic PM & PY */}
                            <Col span={12}>
                                <Form.Item label="Basic PM" name="basic_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Basic PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('basic_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* HRA PM & PY */}
                            <Col span={12}>
                                <Form.Item label="HRA PM" name="hra_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="HRA PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('hra_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Conveyance PM & PY */}
                            <Col span={12}>
                                <Form.Item label="Conveyance PM" name="conveyance_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Conveyance PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('conveyance_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Other Allowance PM & PY */}
                            <Col span={12}>
                                <Form.Item label="Other Allowance PM" name="other_allowance_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Other Allowance PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('other_allowance_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Gross PM & PY */}
                            <Col span={12}>
                                <Form.Item label="Gross PM" name="gross_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Gross PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('gross_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* PF Employer PM & PY */}
                            <Col span={12}>
                                <Form.Item label="PF Employer PM" name="pf_employer_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="PF Employer PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('pf_employer_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* ESI Employer PM & PY */}
                            <Col span={12}>
                                <Form.Item label="ESI Employer PM" name="esi_employer_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="ESI Employer PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('esi_employer_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* CTC PM & PY */}
                            <Col span={12}>
                                <Form.Item label="CTC PM" name="ctc_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="CTC PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('ctc_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* PF Employee PM & PY */}
                            <Col span={12}>
                                <Form.Item label="PF Employee PM" name="pf_employee_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="PF Employee PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('pf_employee_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* ESI Employee PM & PY */}
                            <Col span={12}>
                                <Form.Item label="ESI Employee PM" name="esi_employee_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="ESI Employee PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('esi_employee_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* PT PM & PY */}
                            <Col span={12}>
                                <Form.Item label="PT PM" name="pt_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="PT PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('pt_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Deductions PM & PY */}
                            <Col span={12}>
                                <Form.Item label="Deductions PM" name="deductions_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Deductions PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('deductions_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Net Pay PM & PY */}
                            <Col span={12}>
                                <Form.Item label="Net Pay PM" name="net_pay_pm">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Net Pay PY">
                                    <InputNumber 
                                        style={{ width: "100%" }} 
                                        disabled 
                                        value={(form.getFieldValue('net_pay_pm') || 0) * 12}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <div style={{ textAlign: "right", marginTop: 20 }}>
                            <Button onClick={handleCloseModal} style={{ marginRight: 10 }}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                style={{
                                    backgroundColor: "#0e497b",
                                    borderColor: "#0e497b",
                                    fontWeight: "500",
                                }}
                            >
                                Save
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />

            <center>
                <h5 style={{ marginTop: 20 }}>Fixed Salary</h5>
            </center>

            {/* Filters */}
            <Card style={{ margin: 20 }}>
                <Row gutter={[16, 16]} justify="center">
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Input
                            placeholder="Filter by Emp Code"
                            value={filters.emp_code}
                            allowClear
                            onChange={(e) => setFilters({ ...filters, emp_code: e.target.value || "" })}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Input
                            placeholder="Filter by Name"
                            value={filters.name}
                            allowClear
                            onChange={(e) => setFilters({ ...filters, name: e.target.value || "" })}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Select
                            showSearch
                            placeholder="Search Organisation"
                            style={{ width: "100%" }}
                            value={filters.organisation || undefined}
                            onChange={(value) => setFilters({ ...filters, organisation: value || "", department: "", designation: "" })}
                            allowClear
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {orgList.map((org) => (
                                <Select.Option key={org.id} value={org.name}>
                                    {org.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Select
                            showSearch
                            placeholder="Search Department"
                            style={{ width: "100%" }}
                            value={filters.department || undefined}
                            onChange={(value) => setFilters({ ...filters, department: value || "", designation: "" })}
                            allowClear
                            disabled={!filters.organisation}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {deptList
                                .filter((d) => !filters.organisation || d.organisation_name?.toLowerCase().includes(filters.organisation.toLowerCase()))
                                .map((dept) => (
                                    <Select.Option key={dept.id} value={dept.name}>
                                        {dept.name}
                                    </Select.Option>
                                ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Select
                            showSearch
                            placeholder="Search Designation"
                            style={{ width: "100%" }}
                            value={filters.designation || undefined}
                            onChange={(value) => setFilters({ ...filters, designation: value || "" })}
                            allowClear
                            disabled={!filters.department}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {desigList
                                .filter((d) => !filters.department || d.department_name?.toLowerCase().includes(filters.department.toLowerCase()))
                                .map((desig) => (
                                    <Select.Option key={desig.id} value={desig.name}>
                                        {desig.name}
                                    </Select.Option>
                                ))}
                        </Select>
                    </Col>

                    <div style={{ display: "flex", gap: "10px", width: "100%", justifyContent: "space-between", alignItems: "center", marginTop: "10px", padding: "0 10px" }}>
                        <div style={{ display: "flex", gap: "15px" }}>
                            <Card size="small" style={{ borderRadius: "8px", border: "1px solid #0e497b", backgroundColor: "#f0f5ff" }}>
                                <span style={{ fontWeight: "700", color: "#0e497b" }}>Total: </span>
                                <span style={{ fontWeight: "800", color: "#0e497b", fontSize: "15px" }}>{filteredData.length}</span>
                            </Card>
                            {selectedRowKeys.length > 0 && (
                                <Card size="small" style={{ borderRadius: "8px", border: "1px solid #52c41a", backgroundColor: "#f6ffed" }}>
                                    <span style={{ fontWeight: "700", color: "#52c41a" }}>Selected: </span>
                                    <span style={{ fontWeight: "800", color: "#52c41a", fontSize: "15px" }}>{selectedRowKeys.length}</span>
                                </Card>
                            )}
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            {/* Download Template — blank workbook matching bulk-upload columns */}
                            <Button
                                icon={<DownloadOutlined />}
                                style={{ backgroundColor: "#fff", borderColor: "#0e497b", color: "#0e497b", fontWeight: "500" }}
                                onClick={handleDownloadTemplate}
                            >
                                Download Template
                            </Button>

                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={handleExportExcel}
                                style={{ background: 'green', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                Download Excel
                            </Button>

                            <Button
                                type="primary"
                                danger
                                icon={<FilePdfOutlined />}
                                onClick={handleExportPDF}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                PDF
                            </Button>
                        </div>
                    </div>
                </Row>
            </Card>

            {/* Table */}
            <Card style={{ margin: 20 }}>
                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    bordered
                    scroll={{ x: 5000 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{ backgroundColor: "#0e497b", color: "white", textAlign: "center" }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </Card>
        </>
    );
}